import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Users,
  Download,
  Search,
  CheckCircle,
  XCircle,
  Phone,
  Mail,
  MapPin,
  Ticket,
  DollarSign,
  Calendar,
  User,
  Building,
  Globe,
  Lock,
  Eye,
  EyeOff,
  Clock,
  AlertCircle,
  Trash2,
  ChevronDown,  // ✅ NEW
  ChevronUp,    // ✅ NEW
  UserPlus,      // ✅ NEW
  CreditCard,
  Info
} from 'lucide-react';
import axios from 'axios';

// Move AuthModal outside the main component to prevent re-creation
const AuthModal = ({
  username,
  password,
  showPassword,
  authError,
  onUsernameChange,
  onPasswordChange,
  onTogglePassword,
  onSubmit,
  onKeyPress
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    {/* Backdrop */}
    <div className="absolute inset-0 bg-black bg-opacity-50"></div>

    {/* Modal */}
    <div className="relative bg-white rounded-lg shadow-xl p-8 w-full max-w-md mx-4">
      <div className="text-center mb-6">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
          <Lock className="h-6 w-6 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Admin Login</h2>
        <p className="text-gray-600 mt-2">Please enter your credentials to access participant details</p>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
            Username
          </label>
          <input
            id="username"
            type="text"
            value={username}
            onChange={onUsernameChange}
            onKeyPress={onKeyPress}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter username"
            autoComplete="username"
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={onPasswordChange}
              onKeyPress={onKeyPress}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter password"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={onTogglePassword}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-gray-400" />
              ) : (
                <Eye className="h-4 w-4 text-gray-400" />
              )}
            </button>
          </div>
        </div>

        {authError && (
          <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded">
            {authError}
          </div>
        )}

        <button
          onClick={onSubmit}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Login
        </button>
      </div>
    </div>
  </div>
);

const FullParticipantDetails = () => {
  const { eventId, tab } = useParams();
  const navigate = useNavigate();

  // Authentication states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [pendingParticipants, setPendingParticipants] = useState([]); // ✅ NEW
  const [participants, setParticipants] = useState([]);
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    checkinStatus: 'all',
    ticketCategory: 'all',
    couponUsed: 'all'
  });
  const [expandedOrder, setExpandedOrder] = useState([]);
  const [expandedPendingOrders, setExpandedPendingOrders] = useState([]); // ✅ NEW
  const [creatingParticipants, setCreatingParticipants] = useState({}); // ✅ NEW
  const [razorpayPayments, setRazorpayPayments] = useState([]); // ✅ NEW
  const [razorpayStats, setRazorpayStats] = useState(null); // ✅ NEW
  const [loadingPayments, setLoadingPayments] = useState(false); // ✅ NEW
  const [selectedPayment, setSelectedPayment] = useState(null); // ✅ NEW
  const [showPaymentDetail, setShowPaymentDetail] = useState(false); // ✅ NEW
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const itemsPerPageOptions = [10, 50, 100];

  const currentTab = tab || 'indian';

// ✅ NEW: Add pagination state for Razorpay payments
const [paymentsPage, setPaymentsPage] = useState(1);
const [paymentsLimit, setPaymentsLimit] = useState(100);
const [hasMorePayments, setHasMorePayments] = useState(false);
  // Add toggle state for group/individual view
  const [viewMode, setViewMode] = useState('individual'); // 'individual' or 'grouped'

  const handleResendEmail = async (participantId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/participants/${participantId}/resend-emails`
      );
      alert('Emails resent!');
    } catch (error) {
      alert('Failed to resend emails');
      console.error(error);
    }
  };


const handlePaymentsPrevPage = () => {
  if (paymentsPage > 1) {
    const newPage = paymentsPage - 1;
    setPaymentsPage(newPage);
    fetchRazorpayPayments(newPage, paymentsLimit);
  }
};

const handlePaymentsNextPage = () => {
  if (hasMorePayments) {
    const newPage = paymentsPage + 1;
    setPaymentsPage(newPage);
    fetchRazorpayPayments(newPage, paymentsLimit);
  }
};
const handlePaymentsLimitChange = (newLimit) => {
  setPaymentsLimit(newLimit);
  setPaymentsPage(1); // Reset to first page
  fetchRazorpayPayments(1, newLimit);
};

const fetchRazorpayPayments = async (page = paymentsPage, limit = paymentsLimit) => {
  try {
    setLoadingPayments(true);
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/payment/razorpay-payments/${eventId}`,
      {
        params: {
          page: page,
          limit: limit
        }
      }
    );
    
    setRazorpayPayments(response.data.payments || []);
    setRazorpayStats(response.data.stats || null);
    setHasMorePayments(response.data.pagination?.hasMore || false); // ✅ NEW
  } catch (error) {
    console.error('Error fetching Razorpay payments:', error);
    setRazorpayPayments([]);
    setRazorpayStats(null);
  } finally {
    setLoadingPayments(false);
  }
};

  // ✅ NEW: Fetch payment detail
  const fetchPaymentDetail = async (paymentId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/payment/razorpay-payment-detail/${eventId}/${paymentId}`
      );

      setSelectedPayment(response.data);
      setShowPaymentDetail(true);
    } catch (error) {
      console.error('Error fetching payment detail:', error);
      alert('Failed to fetch payment details');
    }
  };
  // ✅ NEW: Manually create participants from pending order
  const handleManuallyCreateParticipants = async (orderId) => {
    if (!window.confirm('Are you sure you want to manually create participants for this order? This will send emails and generate ID cards.')) {
      return;
    }

    try {
      setCreatingParticipants(prev => ({ ...prev, [orderId]: true }));

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/payment/manually-create-participants/${orderId}`
      );

      alert(`✅ Successfully created ${response.data.count} participant(s)!`);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error manually creating participants:', error);
      alert('❌ Failed to create participants: ' + (error.response?.data?.error || error.message));
    } finally {
      setCreatingParticipants(prev => ({ ...prev, [orderId]: false }));
    }
  };

useEffect(() => {
  if (currentTab === 'payments' && isAuthenticated) {
    setPaymentsPage(1); // ✅ Reset to page 1 when switching to payments tab
    fetchRazorpayPayments(1, paymentsLimit);
  }
}, [currentTab, isAuthenticated]);

  // Check authentication on component mount
  useEffect(() => {
    const storedAuth = localStorage.getItem('insideout_admin_auth');
    if (storedAuth) {
      const correctAuth = btoa('insideoutadmin:AdminInsideout@2025');
      if (storedAuth === correctAuth) {
        setIsAuthenticated(true);
        setShowAuthModal(false);
      } else {
        localStorage.removeItem('insideout_admin_auth');
      }
    }
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
      setCurrentPage(1);
    }
  }, [eventId, isAuthenticated]);

  // Memoized event handlers to prevent re-creation
  const handleUsernameChange = useCallback((e) => {
    setUsername(e.target.value);
    setAuthError(''); // Clear error when user types
  }, []);

  const handlePasswordChange = useCallback((e) => {
    setPassword(e.target.value);
    setAuthError(''); // Clear error when user types
  }, []);

  const handleTogglePassword = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const handleAuthentication = useCallback(() => {
    const correctUsername = 'insideoutadmin';
    const correctPassword = 'AdminInsideout@2025';

    if (username === correctUsername && password === correctPassword) {
      const authToken = btoa(`${username}:${password}`);
      localStorage.setItem('insideout_admin_auth', authToken);
      setIsAuthenticated(true);
      setShowAuthModal(false);
      setAuthError('');
      // Clear form
      setUsername('');
      setPassword('');
    } else {
      setAuthError('Invalid username or password');
    }
  }, [username, password]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handleAuthentication();
    }
  }, [handleAuthentication]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('insideout_admin_auth');
    setIsAuthenticated(false);
    setShowAuthModal(true);
    setUsername('');
    setPassword('');
    setAuthError('');
    setShowPassword(false);
  }, []);

  // Update the fetchData function to include Razorpay payments
  const fetchData = async () => {
    try {
      setLoading(true);
      const [participantsResponse, eventResponse, pendingResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/participants/event/${eventId}`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/events/${eventId}`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/payment/pending-participants/${eventId}`)
      ]);

      setParticipants(participantsResponse.data);
      setEventData(eventResponse.data);
      setPendingParticipants(pendingResponse.data.pendingParticipants || []);

      // ✅ Fetch Razorpay payments if tab is "payments"
      if (currentTab === 'payments') {
        await fetchRazorpayPayments();
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Trigger Razorpay payments fetch when tab changes to "payments"
  useEffect(() => {
    if (currentTab === 'payments' && isAuthenticated) {
      fetchRazorpayPayments();
    }
  }, [currentTab, isAuthenticated]);
  // ✅ NEW: Handler to cancel pending participant
  const handleCancelPending = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this Pending Participants?')) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/payment/cancel-pending-participant/${orderId}`
      );
      alert('Pending Participants cancelled successfully');
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error cancelling Pending Participants:', error);
      alert('Failed to cancel Pending Participants');
    }
  };


  // ✅ NEW: Toggle expanded pending order
  const togglePendingOrder = (orderId) => {
    setExpandedPendingOrders(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  // ✅ NEW: Flatten pending participants for display
  const flattenedPendingParticipants = useMemo(() => {
    const flattened = [];

    pendingParticipants.forEach(pending => {
      pending.participantsData.forEach(participant => {
        flattened.push({
          ...participant,
          orderId: pending.orderId,
          razorpayOrderId: pending.razorpayOrderId,
          totalAmount: pending.totalAmount,
          currency: pending.currency,
          couponUsed: pending.useCouponCode,
          couponDiscount: pending.couponDiscount,
          createdAt: pending.createdAt,
          expiresAt: pending.expiresAt,
          status: pending.status,
          eventName: pending.eventName
        });
      });
    });

    return flattened;
  }, [pendingParticipants]);



  // ✅ NEW: Filter pending participants by region
  const filteredPendingParticipants = useMemo(() => {
    if (currentTab === 'pending') {
      return flattenedPendingParticipants;
    }

    return flattenedPendingParticipants.filter(p =>
      currentTab === 'indian' ? p.ticketRegion === 'indian' : p.ticketRegion === 'international'
    );
  }, [flattenedPendingParticipants, currentTab]);



  // Helper function to group participants by order
  const groupParticipantsByOrder = (participants) => {
    const orderGroups = {};

    participants.forEach(participant => {
      const orderId = participant.ticketDetails?.orderInfo?.orderId;
      if (!orderId) return;

      if (!orderGroups[orderId]) {
        orderGroups[orderId] = {
          orderId,
          participants: [],
          count: 0,
          totalAmount: participant.ticketDetails?.orderInfo?.totalOrderAmount || 0,
          paymentStatus: participant.ticketDetails?.orderInfo?.paymentStatus,
          couponUsed: participant.ticketDetails?.orderInfo?.couponUsed,
          couponDiscount: participant.ticketDetails?.orderInfo?.couponDiscount || 0,
          purchaseDate: participant.ticketDetails?.purchaseDate
        };
      }

      orderGroups[orderId].participants.push(participant);
      orderGroups[orderId].count = orderGroups[orderId].participants.length;
    });

    return Object.values(orderGroups).sort((a, b) =>
      new Date(b.purchaseDate) - new Date(a.purchaseDate)
    );
  };

  // Filtering
  const filteredParticipants = useMemo(() => {
    // Sort by createdAt descending (latest first)
    let sorted = [...participants].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    // Apply tab (region) filter
    let filtered = sorted.filter(p =>
      currentTab === 'indian' ? p.ticketRegion === 'indian' : p.ticketRegion === 'international'
    );

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(p =>
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.phone || '').includes(searchTerm) ||
        (p.institute || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filters
    if (filters.checkinStatus !== 'all') {
      filtered = filtered.filter(p =>
        filters.checkinStatus === 'checked-in' ? p.checkin : !p.checkin
      );
    }
    if (filters.ticketCategory !== 'all') {
      filtered = filtered.filter(p => p.ticketCategory === filters.ticketCategory);
    }
    if (filters.couponUsed !== 'all') {
      filtered = filtered.filter(p =>
        filters.couponUsed === 'yes' ? p.useCouponCode : !p.useCouponCode
      );
    }

    return filtered;
  }, [participants, currentTab, searchTerm, filters]);

  // Add this to your existing useMemo for filteredParticipants
  const groupedOrders = useMemo(() => {
    return groupParticipantsByOrder(filteredParticipants);
  }, [filteredParticipants]);

  // Pagination
  const paginatedParticipants = useMemo(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    return filteredParticipants.slice(startIdx, startIdx + itemsPerPage);
  }, [filteredParticipants, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredParticipants.length / itemsPerPage);

  // Unique categories for filters
  const uniqueTicketCategories = [...new Set(participants.map(p => p.ticketCategory).filter(Boolean))];

  // Export as CSV
  const exportToCSV = () => {
    const headers = [
      'Full Name', 'Email', 'Phone', 'Country Code', 'City', 'State/Country',
      'Institute', 'Designation', 'Ticket Category', 'Ticket Region',
      'Original Price', 'Discounted Price', 'Currency', 'Coupon Used',
      'Coupon Discount', 'Checkin Status', 'Registration Date',
       'UTM Source', 'UTM Medium', 'UTM Campaign', 'UTM Term', 'UTM Content', 'UTM ID'
    ];
    const csvData = filteredParticipants.map(p => [
      `${p.firstName} ${p.lastName || ''}`.trim(),
      p.email || '',
      p.phone || '',
      p.countryCode || '',
      p.city || '',
      p.ticketRegion === 'indian' ? p.state : p.country,
      p.institute || '',
      p.designation || '',
      p.ticketCategory || '',
      p.ticketRegion || '',
      p.ticketDetails?.originalPrice || 0,
      p.ticketDetails?.discountedPrice || 0,
      p.ticketDetails?.currency || '',
      p.useCouponCode || 'No',
      p.ticketDetails?.orderInfo?.couponDiscount || 0,
      p.checkin ? 'Checked In' : 'Not Checked In',
      new Date(p.createdAt).toLocaleDateString(),
       // ✅ NEW: UTM Parameters
    p.utmSource || 'N/A',
    p.utmMedium || 'N/A',
    p.utmCampaign || 'N/A',
    p.utmTerm || 'N/A',
    p.utmContent || 'N/A',
    p.utm_id || 'N/A'

    ]);
    const csvContent = [headers, ...csvData].map(row =>
      row.map(field => `"${field}"`).join(',')
    ).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${eventData?.eventName || 'event'}_${currentTab}_participants.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Show loading state
  if (loading && isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Authentication Modal */}
      {showAuthModal && (
        <AuthModal
          username={username}
          password={password}
          showPassword={showPassword}
          authError={authError}
          onUsernameChange={handleUsernameChange}
          onPasswordChange={handlePasswordChange}
          onTogglePassword={handleTogglePassword}
          onSubmit={handleAuthentication}
          onKeyPress={handleKeyPress}
        />
      )}

      {/* Main Content - Blurred when modal is open */}
      <div className={`${showAuthModal ? 'blur-sm pointer-events-none' : ''} transition-all duration-200`}>
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  <Users className="w-8 h-8 text-blue-600" />
                  {eventData?.eventName} - Participant Details
                </h1>
                <p className="text-gray-600 mt-2">
                  Manage and view detailed participant information
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={exportToCSV}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors"
                >
                  <Download className="w-5 h-5" />
                  Export CSV
                </button>
                <button
                  onClick={handleLogout}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors"
                >
                  <Lock className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-2xl font-bold text-blue-600">{filteredParticipants.length}</div>
              <div className="text-sm text-gray-600">Total Participants</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-2xl font-bold text-green-600">
                {filteredParticipants.filter(p => p.checkin).length}
              </div>
              <div className="text-sm text-gray-600">Checked In</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-2xl font-bold text-purple-600">
                {groupedOrders.length}
              </div>
              <div className="text-sm text-gray-600">Total Orders</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-2xl font-bold text-orange-600">
                {groupedOrders.filter(order => order.count > 1).length}
              </div>
              <div className="text-sm text-gray-600">Multi-Participant Orders</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-2xl font-bold text-red-600">
                {filteredParticipants.filter(p => p.ticketDetails?.orderInfo?.couponUsed).length}
              </div>
              <div className="text-sm text-gray-600">Used Coupons</div>
            </div>
            {/* ✅ NEW: Pending Participantsnts Stat */}
            <div className="bg-white rounded-lg shadow-sm border border-amber-200 p-4">
              <div className="text-2xl font-bold text-amber-600">
                {pendingParticipants.length}
              </div>
              <div className="text-sm text-gray-600">Pending Participants</div>
            </div>

          </div>

          {/* ✅ UPDATED: Tabs with Pending */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => {
                  navigate(`/full-participant-details/${eventId}/indian`);
                  setCurrentPage(1);
                }}
                className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors ${currentTab === 'indian'
                  ? 'border-blue-600 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Indian Participants ({participants.filter(p => p.ticketRegion === 'indian').length})
                </div>
              </button>
              <button
                onClick={() => {
                  navigate(`/full-participant-details/${eventId}/international`);
                  setCurrentPage(1);
                }}
                className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors ${currentTab === 'international'
                  ? 'border-blue-600 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  International Participants ({participants.filter(p => p.ticketRegion === 'international').length})
                </div>
              </button>
              {/* ✅ NEW: Pending Tab */}
              <button
                onClick={() => {
                  navigate(`/full-participant-details/${eventId}/pending`);
                  setCurrentPage(1);
                }}
                className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors ${currentTab === 'pending'
                  ? 'border-amber-600 text-amber-600 bg-amber-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Pending Participants ({pendingParticipants.length})
                  {pendingParticipants.length > 0 && (
                    <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {pendingParticipants.length}
                    </span>
                  )}
                </div>
              </button>

              {/* ✅ NEW: All Payments Tab */}
              {/* <button
                onClick={() => {
                  navigate(`/full-participant-details/${eventId}/payments`);
                  setCurrentPage(1);
                }}
                className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${currentTab === 'payments'
                  ? 'border-green-600 text-green-600 bg-green-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  All Payments
                  {razorpayStats && (
                    <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {razorpayStats.totalPayments}
                    </span>
                  )}
                </div>
              </button> */}
            </div>
          </div>

          {/* Items per page and pagination controls */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-2">
            <div>
              <label htmlFor="itemsPerPage" className="mr-2 font-medium text-sm text-gray-700">
                Show
              </label>
              <select
                id="itemsPerPage"
                value={itemsPerPage}
                onChange={e => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {itemsPerPageOptions.map(opt => (
                  <option value={opt} key={opt}>{opt} / page</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 border rounded hover:bg-blue-100 disabled:opacity-50"
              >
                Prev
              </button>
              <span className="text-sm text-gray-800">
                Page {currentPage} of {totalPages || 1}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-2 py-1 border rounded hover:bg-blue-100 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>



          {
            currentTab === 'payments' ? (
              // ✅ NEW: All Payments View
              <div className="bg-white rounded-lg shadow-sm border border-green-200 overflow-hidden">
                <div className="bg-green-50 p-4 border-b border-green-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-green-800">
                      <CreditCard className="w-5 h-5" />
                      <span className="font-medium">
                        All Razorpay Payments - Direct from Razorpay API
                      </span>
                    </div>
                    {razorpayStats && (
                      <div className="flex items-center gap-4 text-sm">
                        <div className="bg-white px-3 py-1 rounded border border-green-300">
                          <span className="text-gray-600">Total: </span>
                          <span className="font-bold text-green-700">
                            {razorpayStats.totalAmount.toLocaleString()} INR
                          </span>
                        </div>
                        <div className="bg-white px-3 py-1 rounded border border-green-300">
                          <span className="text-gray-600">With Participants: </span>
                          <span className="font-bold text-green-700">
                            {razorpayStats.paymentsWithParticipants}
                          </span>
                        </div>
                        <div className="bg-white px-3 py-1 rounded border border-amber-300">
                          <span className="text-gray-600">Missing Participants: </span>
                          <span className="font-bold text-amber-700">
                            {razorpayStats.paymentsWithoutParticipants}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                
    {/* ✅ NEW: Pagination Controls at Top */}
    <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <label htmlFor="paymentsLimit" className="text-sm font-medium text-gray-700">
          Show:
        </label>
        <select
          id="paymentsLimit"
          value={paymentsLimit}
          onChange={(e) => handlePaymentsLimitChange(Number(e.target.value))}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          disabled={loadingPayments}
        >
          <option value={50}>50 payments</option>
          <option value={100}>100 payments</option>
          <option value={200}>200 payments</option>
        </select>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-600">
          Page {paymentsPage} • Showing {razorpayPayments.length} payments
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePaymentsPrevPage}
            disabled={paymentsPage === 1 || loadingPayments}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <button
            onClick={handlePaymentsNextPage}
            disabled={!hasMorePayments || loadingPayments}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>

                {loadingPayments ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Payment ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Customer Details
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Payment Method
                          </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                           Description
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date & Time
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {razorpayPayments.map((payment, index) => (
                          <tr
                            key={payment.razorpayPaymentId}
                            className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-green-50 transition-colors`}
                          >
                            <td className="px-6 py-4">
                              <div className="space-y-1">
                                <div className="text-sm font-mono font-medium text-gray-900">
                                  {payment.razorpayPaymentId}
                                </div>
                                {payment.razorpayOrderId && (
                                  <div className="text-xs text-gray-500 font-mono">
                                    Order: {payment.razorpayOrderId}
                                  </div>
                                )}
                              </div>
                            </td>

                            <td className="px-6 py-4">
                              <div className="space-y-1">
                                <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {payment.customerName}
                                </div>
                                {payment.email !== 'N/A' && (
                                  <div className="text-xs text-gray-600 flex items-center gap-1">
                                    <Mail className="w-3 h-3" />
                                    {payment.email}
                                  </div>
                                )}
                                {payment.contact !== 'N/A' && (
                                  <div className="text-xs text-gray-600 flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {payment.contact}
                                  </div>
                                )}
                              </div>
                            </td>

                            <td className="px-6 py-4">
                              <div className="text-sm font-bold text-gray-900 flex items-center gap-1">
                                <DollarSign className="w-3 h-3" />
                                {payment.currency} {payment.amount.toLocaleString()}
                              </div>
                            </td>

                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${payment.method === 'card' ? 'bg-blue-100 text-blue-800' :
                                  payment.method === 'upi' ? 'bg-purple-100 text-purple-800' :
                                    payment.method === 'netbanking' ? 'bg-green-100 text-green-800' :
                                      payment.method === 'wallet' ? 'bg-orange-100 text-orange-800' :
                                        'bg-gray-100 text-gray-800'
                                }`}>
                                {payment.method}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              {payment.description}
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(payment.createdAt).toLocaleDateString()}
                              </div>
                              <div className="text-xs text-gray-500">
                                {new Date(payment.createdAt).toLocaleTimeString()}
                              </div>
                            </td>

                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${payment.status === 'captured' ? 'bg-green-100 text-green-800' :
                                    payment.status === 'authorized' ? 'bg-blue-100 text-blue-800' :
                                      'bg-gray-100 text-gray-800'
                                  }`}>
                                  {payment.status}
                                </span>
                                {payment.hasParticipant ? (
                                  <CheckCircle className="w-4 h-4 text-green-600" title="Has participant" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-amber-600" title="No participant found" />
                                )}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => fetchPaymentDetail(payment.razorpayPaymentId)}
                                className="text-blue-600 hover:text-blue-900 text-xs font-medium px-3 py-1.5 rounded hover:bg-blue-50 transition-colors flex items-center gap-1"
                              >
                                <Info className="w-3 h-3" />
                                Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {!loadingPayments && razorpayPayments.length === 0 && (
                  <div className="text-center py-12">
                    <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">No payments found</p>
                    <p className="text-gray-400">Payments will appear here once customers make purchases</p>
                  </div>
                )}
              </div>
            )
              :
              currentTab === 'pending' ? (
                // Pending Participants View
                <div className="bg-white rounded-lg shadow-sm border border-amber-200 overflow-hidden">
                  <div className="bg-amber-50 p-4 border-b border-amber-200">
                    <div className="flex items-center gap-2 text-amber-800">
                      <AlertCircle className="w-5 h-5" />
                      <span className="font-medium">
                        Pending Participants - Click on any row to see participant details
                      </span>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                            {/* Expand icon */}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Order Details
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Participants Count
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Expires
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {pendingParticipants.map((pending, index) => (
                          <React.Fragment key={pending.orderId}>
                            {/* Main Row - Clickable */}
                            <tr
                              className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} cursor-pointer hover:bg-amber-50 transition-colors`}
                              onClick={() => togglePendingOrder(pending.orderId)}
                            >
                              <td className="px-6 py-4">
                                {expandedPendingOrders.includes(pending.orderId) ? (
                                  <ChevronUp className="w-5 h-5 text-gray-600" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-gray-600" />
                                )}
                              </td>

                              <td className="px-6 py-4">
                                <div className="space-y-1">
                                  <div className="text-sm font-medium text-gray-900">
                                    {pending.orderId}
                                  </div>
                                  <div className="text-xs text-gray-500 font-mono">
                                    Razorpay: {pending.razorpayOrderId}
                                  </div>
                                  {pending.useCouponCode && (
                                    <span className="inline-block bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">
                                      {pending.useCouponCode}
                                    </span>
                                  )}
                                </div>
                              </td>

                              <td className="px-6 py-4">
                                <div className="flex items-center">
                                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                    {pending.participantsData.length} participant{pending.participantsData.length > 1 ? 's' : ''}
                                  </span>
                                </div>
                              </td>

                              <td className="px-6 py-4">
                                <div className="space-y-1">
                                  <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
                                    <DollarSign className="w-3 h-3" />
                                    {pending.currency} {pending.totalAmount}
                                  </div>
                                  {pending.couponDiscount > 0 && (
                                    <div className="text-xs text-green-600">
                                      Discount: {pending.currency} {pending.couponDiscount}
                                    </div>
                                  )}
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(pending.createdAt).toLocaleDateString()}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {new Date(pending.createdAt).toLocaleTimeString()}
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-amber-600 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {new Date(pending.expiresAt).toLocaleDateString()}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {new Date(pending.expiresAt).toLocaleTimeString()}
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center gap-2">
                                  {/* ✅ NEW: Create Participants Button */}
                                  <button
                                    onClick={() => handleManuallyCreateParticipants(pending.orderId)}
                                    disabled={creatingParticipants[pending.orderId]}
                                    className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1 transition-colors"
                                    title="Manually create participants"
                                  >
                                    {creatingParticipants[pending.orderId] ? (
                                      <>
                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                        Creating...
                                      </>
                                    ) : (
                                      <>
                                        <UserPlus className="w-3 h-3" />
                                        Create
                                      </>
                                    )}
                                  </button>

                                  <button
                                    onClick={() => handleCancelPending(pending.orderId)}
                                    className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50 transition-colors"
                                    title="Cancel Pending Participants"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>

                            {/* ✅ NEW: Expanded Details Row */}
                            {expandedPendingOrders.includes(pending.orderId) && (
                              <tr>
                                <td colSpan="7" className="px-6 py-6 bg-gradient-to-r from-amber-50 to-yellow-50 border-t-2 border-amber-300">
                                  <div className="space-y-4">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                      <User className="w-5 h-5 text-amber-600" />
                                      Participant Details for Order: {pending.orderId}
                                    </h4>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                      {pending.participantsData.map((participant, idx) => (
                                        <div
                                          key={idx}
                                          className="bg-white border-2 border-amber-200 rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow"
                                        >
                                          {/* Participant Header */}
                                          <div className="flex items-start mb-3 pb-3 border-b border-gray-200">
                                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center mr-3 flex-shrink-0">
                                              <User className="w-5 h-5 text-white" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <div className="font-bold text-gray-900 truncate text-base">
                                                {participant.firstName} {participant.lastName || ''}
                                              </div>
                                              <div className="text-xs text-gray-500 truncate">
                                                Participant #{idx + 1}
                                              </div>
                                            </div>
                                          </div>

                                          {/* Participant Details */}
                                          <div className="space-y-2.5">
                                            {/* Email */}
                                            <div className="flex items-start text-sm">
                                              <Mail className="w-4 h-4 mr-2 mt-0.5 text-blue-600 flex-shrink-0" />
                                              <div className="flex-1 min-w-0">
                                                <div className="text-xs text-gray-500">Email</div>
                                                <div className="text-gray-900 truncate" title={participant.email}>
                                                  {participant.email || 'Not provided'}
                                                </div>
                                              </div>
                                            </div>

                                            {/* Phone */}
                                            <div className="flex items-start text-sm">
                                              <Phone className="w-4 h-4 mr-2 mt-0.5 text-green-600 flex-shrink-0" />
                                              <div className="flex-1">
                                                <div className="text-xs text-gray-500">Phone</div>
                                                <div className="text-gray-900">
                                                  {participant.countryCode ? `+${participant.countryCode}` : ''} {participant.phone || 'Not provided'}
                                                </div>
                                              </div>
                                            </div>

                                            {/* Location */}
                                            <div className="flex items-start text-sm">
                                              <MapPin className="w-4 h-4 mr-2 mt-0.5 text-red-600 flex-shrink-0" />
                                              <div className="flex-1">
                                                <div className="text-xs text-gray-500">Location</div>
                                                <div className="text-gray-900">
                                                  {participant.city || 'N/A'}
                                                  {participant.state && `, ${participant.state}`}
                                                  {participant.country && `, ${participant.country}`}
                                                </div>
                                              </div>
                                            </div>

                                            {/* Institute */}
                                            {participant.institute && (
                                              <div className="flex items-start text-sm">
                                                <Building className="w-4 h-4 mr-2 mt-0.5 text-purple-600 flex-shrink-0" />
                                                <div className="flex-1">
                                                  <div className="text-xs text-gray-500">Institute</div>
                                                  <div className="text-gray-900">{participant.institute}</div>
                                                </div>
                                              </div>
                                            )}

                                            {/* Ticket Info */}
                                            <div className="mt-3 pt-3 border-t border-gray-200">
                                              <div className="flex items-center justify-between">
                                                <div className="flex items-center text-sm">
                                                  <Ticket className="w-4 h-4 mr-1 text-amber-600" />
                                                  <span className="text-gray-700 font-medium">
                                                    {participant.ticketCategory || 'N/A'}
                                                  </span>
                                                </div>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${participant.ticketRegion === 'indian'
                                                  ? 'bg-green-100 text-green-800'
                                                  : 'bg-blue-100 text-blue-800'
                                                  }`}>
                                                  {participant.ticketRegion || 'N/A'}
                                                </span>
                                              </div>

                                              {participant.discountedPrice && (
                                                <div className="mt-2 flex items-center text-sm text-gray-600">
                                                  <DollarSign className="w-4 h-4 mr-1" />
                                                  <span className="font-semibold">
                                                    {participant.currency || 'INR'} {participant.discountedPrice}
                                                  </span>
                                                </div>
                                              )}
                                            </div>

                                            {/* Selected Dates */}
                                            {participant.selectedDates && participant.selectedDates.length > 0 && (
                                              <div className="mt-2 pt-2 border-t border-gray-200">
                                                <div className="text-xs text-gray-500 mb-1">Selected Dates:</div>
                                                <div className="flex flex-wrap gap-1">
                                                  {participant.selectedDates.map((date, dateIdx) => (
                                                    <span
                                                      key={dateIdx}
                                                      className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs"
                                                    >
                                                      {new Date(date).toLocaleDateString()}
                                                    </span>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>

                                    {/* Order Summary at Bottom */}
                                    <div className="mt-6 bg-white border border-amber-300 rounded-lg p-4">
                                      <h5 className="font-semibold text-gray-900 mb-2">Order Summary</h5>
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                        <div>
                                          <div className="text-gray-500">Total Amount</div>
                                          <div className="font-bold text-gray-900">
                                            {pending.currency} {pending.totalAmount}
                                          </div>
                                        </div>
                                        {pending.couponDiscount > 0 && (
                                          <div>
                                            <div className="text-gray-500">Discount</div>
                                            <div className="font-bold text-green-600">
                                              - {pending.currency} {pending.couponDiscount}
                                            </div>
                                          </div>
                                        )}
                                        <div>
                                          <div className="text-gray-500">Razorpay Order ID</div>
                                          <div className="font-mono text-xs text-gray-900 break-all">
                                            {pending.razorpayOrderId}
                                          </div>
                                        </div>
                                        <div>
                                          <div className="text-gray-500">Status</div>
                                          <div className="font-semibold text-amber-600">Pending</div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {pendingParticipants.length === 0 && (
                    <div className="text-center py-12">
                      <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">No pending participants</p>
                      <p className="text-gray-400">All payments have been completed or expired</p>
                    </div>
                  )}
                </div>
              ) : (
                // Existing participants view (keep your existing code)
                <>
                  {/* Filters */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Search */}
                      <div className="relative">
                        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search participants..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                          }}
                          className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full"
                        />
                      </div>

                      {/* Checkin Status Filter */}
                      <select
                        value={filters.checkinStatus}
                        onChange={(e) => {
                          setFilters({ ...filters, checkinStatus: e.target.value });
                          setCurrentPage(1);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">All Status</option>
                        <option value="checked-in">Checked In</option>
                        <option value="not-checked-in">Not Checked In</option>
                      </select>

                      {/* Ticket Category Filter */}
                      <select
                        value={filters.ticketCategory}
                        onChange={(e) => {
                          setFilters({ ...filters, ticketCategory: e.target.value });
                          setCurrentPage(1);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">All Categories</option>
                        {uniqueTicketCategories.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>

                      {/* Coupon Filter */}
                      <select
                        value={filters.couponUsed}
                        onChange={(e) => {
                          setFilters({ ...filters, couponUsed: e.target.value });
                          setCurrentPage(1);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">All Coupon Status</option>
                        <option value="yes">Used Coupon</option>
                        <option value="no">No Coupon</option>
                      </select>
                    </div>
                  </div>

                  {/* Add view toggle buttons before the table */}
                  <div className="mb-4 flex gap-2">
                    <button
                      onClick={() => setViewMode('individual')}
                      className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'individual'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                      Individual View
                    </button>
                    <button
                      onClick={() => setViewMode('grouped')}
                      className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'grouped'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                      Grouped by Order ({groupedOrders.length} orders)
                    </button>
                  </div>
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      {viewMode === 'individual' ? (
                        // Individual table
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Participant
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Contact
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Location
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Ticket Details
                              </th>
                              {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ticket Price
                      </th> */}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          UTM Source
        </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Coupon Used
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedParticipants.map((participant, index) => (
                              <tr key={participant._id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 h-10 w-10">
                                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                        <User className="w-5 h-5 text-blue-600" />
                                      </div>
                                    </div>
                                    <div className="ml-4">
                                      <div className="text-sm font-medium capitalize text-gray-900">
                                        {participant.firstName} {participant.lastName || ''}
                                      </div>
                                    </div>
                                  </div>
                                </td>

                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="space-y-1">
                                    <div className="text-sm text-gray-900 flex items-center gap-1">
                                      <Mail className="w-3 h-3" />
                                      {participant.email || 'N/A'}
                                    </div>
                                    <div className="text-sm text-gray-500 flex items-center gap-1">
                                      <Phone className="w-3 h-3" />
                                      {participant.countryCode} {participant.phone || 'N/A'}
                                    </div>
                                  </div>
                                </td>

                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {participant.city || 'N/A'}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {participant.ticketRegion === 'indian' ? participant.state : participant.country}
                                  </div>
                                </td>

                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="space-y-1">
                                    <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
                                      <Ticket className="w-3 h-3" />
                                      {participant.ticketCategory || 'N/A'}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      Region: {participant.ticketRegion || 'N/A'}
                                    </div>
                                  </div>
                                </td>

                                {/* <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-1">
                            <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              {participant.ticketDetails?.currency} {participant.ticketDetails?.discountedPrice || 0}
                            </div>
                            {participant.ticketDetails?.originalPrice !== participant.ticketDetails?.discountedPrice && (
                              <div className="text-xs text-gray-500 line-through">
                                {participant.ticketDetails?.currency} {participant.ticketDetails?.originalPrice}
                              </div>
                            )}
                          </div>
                        </td> */}
                          <td className="px-6 py-4 whitespace-nowrap">
            <div className="space-y-1">
              {participant.utmSource ? (
                <>
                  <div className="text-sm font-medium text-gray-900">
                    {participant.utmSource}
                  </div>
                  {participant.utmMedium && (
                    <div className="text-xs text-gray-500">
                      via {participant.utmMedium}
                    </div>
                  )}
                  {participant.utmCampaign && (
                    <div className="text-xs text-blue-600">
                      {participant.utmCampaign}
                    </div>
                  )}
                </>
              ) : (
                <span className="text-gray-400 text-xs">Direct</span>
              )}
            </div>
          </td>

                                <td className="px-6 py-4 whitespace-nowrap">
                                  {participant.useCouponCode ? (
                                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">{participant.useCouponCode}</span>
                                  ) : (
                                    <span className="text-gray-400 text-xs">No</span>
                                  )}
                                </td>

                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="space-y-2">
                                    <div className="flex items-center">
                                      {participant.checkin ? (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                          <CheckCircle className="w-3 h-3 mr-1" />
                                          Checked In
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                          <XCircle className="w-3 h-3 mr-1" />
                                          Not Checked In
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-500 flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {new Date(participant.createdAt).toLocaleDateString()}
                                    </div>
                                  </div>
                                </td>
                                <button
                                  className="bg-blue-600 hover:bg-blue-800 text-white px-3 py-1 rounded"
                                  onClick={() => handleResendEmail(participant._id)}
                                >
                                  Send Email
                                </button>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        // Grouped table with inline details
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Order ID
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Participants Count
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Total Amount
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Payment Status
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Coupon Details
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Purchase Date
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {groupedOrders.map((orderGroup, index) => (
                              <React.Fragment key={orderGroup.orderId}>
                                {/* Main order row */}
                                <tr className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">
                                      {orderGroup.orderId}
                                    </div>
                                  </td>

                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                        {orderGroup.count} participant{orderGroup.count > 1 ? 's' : ''}
                                      </span>
                                    </div>
                                  </td>

                                  <td className="px-6 py-4 whitespace-nowrap">
  <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
    <DollarSign className="w-3 h-3" />
    INR {(orderGroup.totalAmount === orderGroup.couponDiscount && orderGroup.paymentMethod =="free")? 0 : orderGroup.totalAmount}
  </div>
  {orderGroup.couponDiscount > 0 && (
    <div className="text-xs text-green-600">
      Saved: INR {orderGroup.couponDiscount}
    </div>
  )}
</td>

                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${orderGroup.paymentStatus === 'completed'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                      }`}>
                                      {orderGroup.paymentStatus}
                                    </span>
                                  </td>

                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {orderGroup.couponUsed ? (
                                      <div className="space-y-1">
                                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                                          {orderGroup.couponUsed}
                                        </span>
                                        <div className="text-xs text-gray-500">
                                          Discount: INR {orderGroup.couponDiscount}
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 text-xs">No coupon</span>
                                    )}
                                  </td>

                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900 flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {new Date(orderGroup.purchaseDate).toLocaleDateString()}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {new Date(orderGroup.purchaseDate).toLocaleTimeString()}
                                    </div>
                                  </td>

                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <button
                                      onClick={() => {
                                        if (expandedOrder.includes(orderGroup.orderId)) {
                                          // Remove from expanded list
                                          setExpandedOrder(expandedOrder.filter(id => id !== orderGroup.orderId));
                                        } else {
                                          // Add to expanded list
                                          setExpandedOrder([...expandedOrder, orderGroup.orderId]);
                                        }
                                      }}
                                      className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                                    >
                                      {expandedOrder.includes(orderGroup.orderId) ? 'Hide' : 'View'} Details
                                    </button>
                                  </td>
                                </tr>

                                {/* Expanded details row */}
                                {expandedOrder.includes(orderGroup.orderId) && (
                                  <tr>
                                    <td colSpan="7" className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                                      <div className="space-y-4">
                                        <h4 className="text-lg font-semibold text-gray-900 mb-4">
                                          Participants in Order: {orderGroup.orderId}
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                          {orderGroup.participants.map(participant => (
                                            <div key={participant._id} className="bg-white border rounded-lg p-4 shadow-sm">
                                              <div className="flex items-center mb-3">
                                                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                                                  <User className="w-4 h-4 text-blue-600" />
                                                </div>
                                                <div>
                                                  <div className="font-medium text-gray-900">
                                                    {participant.firstName} {participant.lastName || ''}
                                                  </div>
                                                  <div className="text-sm text-gray-500">{participant.email}</div>
                                                </div>
                                              </div>

                                              <div className="space-y-2 text-sm">
                                                <div className="flex items-center text-gray-600">
                                                  <Phone className="w-3 h-3 mr-2" />
                                                  {participant.countryCode} {participant.phone}
                                                </div>
                                                <div className="flex items-center text-gray-600">
                                                  <MapPin className="w-3 h-3 mr-2" />
                                                  {participant.city}, {participant.ticketRegion === 'indian' ? participant.state : participant.country}
                                                </div>
                                                <div className="flex items-center text-gray-600">
                                                  <Ticket className="w-3 h-3 mr-2" />
                                                  {participant.ticketCategory}
                                                </div>
                                                <div className="flex items-center text-gray-600">
                                                  <DollarSign className="w-3 h-3 mr-2" />
                                                  {participant.ticketDetails?.currency} {participant.ticketDetails?.discountedPrice || 0}
                                                </div>
                                                <div className="flex items-center mt-2">
                                                  {participant.checkin ? (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                      <CheckCircle className="w-3 h-3 mr-1" />
                                                      Checked In
                                                    </span>
                                                  ) : (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                      <XCircle className="w-3 h-3 mr-1" />
                                                      Not Checked In
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {/* Show empty state only for the active view */}
                    {((viewMode === 'individual' && paginatedParticipants.length === 0) ||
                      (viewMode === 'grouped' && groupedOrders.length === 0)) && (
                        <div className="text-center py-12">
                          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500 text-lg">
                            {viewMode === 'individual' ? 'No participants found' : 'No orders found'}
                          </p>
                          <p className="text-gray-400">Try adjusting your search or filter criteria</p>
                        </div>
                      )}
                  </div>
                </>
              )}




        </div>
      </div>
    </div>
  );
};

export default FullParticipantDetails;
